import { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { LeadWhatsappMessage, LeadNote, WhatsappInstance } from '../types';

// Camada de serviço do chat de WhatsApp de um lead: histórico
// (mensagens + notas internas), envio (texto/mídia via Edge Function
// whatsapp-send), sincronização de histórico (whatsapp-sync-history) e a
// assinatura realtime que mantém o chat atualizado entre atendentes.

export async function markLeadMessagesAsRead(leadId: number): Promise<void> {
  if (!supabase) return;
  await supabase
    .from('lead_whatsapp_messages')
    .update({ status: 'READ' })
    .eq('lead_id', leadId)
    .eq('direction', 'INBOUND')
    .eq('status', 'SENT');
}

export async function fetchUserName(userId: string): Promise<string> {
  if (!supabase) return 'Usuário';
  const { data } = await supabase.from('users').select('name').eq('id', userId).single();
  return data?.name || 'Usuário';
}

export interface ChatSubscriptionHandlers {
  onMessageChange: (message: LeadWhatsappMessage) => void;
  onNoteInserted: (note: LeadNote) => void;
}

// Assina mudanças em mensagens (INSERT/UPDATE, ex.: status de entrega) e
// inserções de notas internas para este lead. Retorna a função de
// cancelamento da assinatura.
export function subscribeToLeadChat(leadId: number, { onMessageChange, onNoteInserted }: ChatSubscriptionHandlers): () => void {
  if (!supabase) return () => {};
  const client: SupabaseClient = supabase;

  const channel = client
    .channel(`whatsapp_chat_${leadId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'lead_whatsapp_messages', filter: `lead_id=eq.${leadId}` },
      (payload) => {
        const message = payload.new as LeadWhatsappMessage;
        if (message?.id) onMessageChange(message);
      }
    )
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'lead_notes', filter: `lead_id=eq.${leadId}` },
      (payload) => onNoteInserted(payload.new as LeadNote)
    )
    .subscribe((status) => {
      console.log(`Realtime Subscription [whatsapp_chat_${leadId}]:`, status);
    });

  return () => client.removeChannel(channel);
}

export async function fetchAvailableInstances(companyId: number): Promise<WhatsappInstance[]> {
  if (!supabase) return [];
  const { data } = await supabase
    .from('whatsapp_instances')
    .select('*, users(name)')
    .eq('company_id', companyId)
    .eq('status', 'CONNECTED');
  return data || [];
}

export interface ChatHistory {
  messages: LeadWhatsappMessage[];
  notes: (LeadNote & { users: { name: string } })[];
}

// Ids dos leads da empresa com o mesmo telefone (os 8 últimos dígitos, como no webhook). Leads
// duplicados dividem a conversa: o webhook grava no que casar primeiro, então o chat precisa
// juntar as mensagens de todos para não "perder" as que caíram no duplicado.
const leadIdsCache = new Map<string, { at: number; ids: number[] }>();
const LEAD_IDS_TTL_MS = 5 * 60 * 1000;

async function leadIdsDoMesmoTelefone(leadId: number, phone?: string, companyId?: number): Promise<number[]> {
  const digits = (phone || '').replace(/\D/g, '');
  if (!supabase || digits.length < 8 || !companyId) return [leadId];
  const suffix = digits.slice(-8);
  const cacheKey = `${companyId}:${leadId}:${suffix}`;
  const cached = leadIdsCache.get(cacheKey);
  if (cached && Date.now() - cached.at < LEAD_IDS_TTL_MS) return cached.ids;
  const { data } = await supabase
    .from('leads')
    .select('id, phone')
    .eq('company_id', companyId)
    .like('phone', '%' + suffix.split('').join('%') + '%');
  const ids = (data || [])
    .filter(l => (l.phone || '').replace(/\D/g, '').slice(-8) === suffix)
    .map(l => Number(l.id));
  const result = Array.from(new Set([leadId, ...ids]));
  leadIdsCache.set(cacheKey, { at: Date.now(), ids: result });
  return result;
}

export async function fetchChatHistory(leadId: number, phone?: string, companyId?: number): Promise<ChatHistory> {
  if (!supabase) return { messages: [], notes: [] };

  const leadIds = await leadIdsDoMesmoTelefone(leadId, phone, companyId);

  const [messagesRes, notesRes] = await Promise.all([
    // Pega as mais recentes (o limite padrão de 1000 linhas cortava as novas em conversas longas)
    supabase
      .from('lead_whatsapp_messages')
      .select('*, whatsapp_instances(instance_name, display_name, users(name))')
      .in('lead_id', leadIds)
      .order('created_at', { ascending: false })
      .limit(1000),
    supabase
      .from('lead_notes')
      .select('*')
      .eq('lead_id', leadId)
      .order('created_at', { ascending: true })
  ]);

  if (messagesRes.error) throw messagesRes.error;
  if (notesRes.error) throw notesRes.error;

  const noteUserIds = Array.from(new Set((notesRes.data || []).map(n => n.user_id).filter(Boolean)));
  const { data: noteUsers } = noteUserIds.length
    ? await supabase.from('users').select('id, name').in('id', noteUserIds)
    : { data: [] as { id: string; name: string }[] };
  const usersMap = new Map(noteUsers?.map(u => [u.id, u.name]) || []);
  const notes = (notesRes.data || []).map(n => ({
    ...n,
    users: { name: usersMap.get(n.user_id) || 'Usuário' }
  }));

  return { messages: [...(messagesRes.data || [])].reverse(), notes };
}

export async function addLeadNote(leadId: number, userId: string, content: string) {
  if (!supabase) throw new Error('Supabase not initialized');
  const { data, error } = await supabase
    .from('lead_notes')
    .insert({ lead_id: leadId, user_id: userId, content })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function uploadWhatsappMedia(leadId: number, file: File): Promise<string | null> {
  if (!supabase) return null;
  const fileName = `${leadId}/${Date.now()}-${file.name}`;
  const { data: uploadData, error: uploadError } = await supabase.storage
    .from('whatsapp_media')
    .upload(fileName, file, { upsert: true });

  if (uploadError || !uploadData) return null;

  const { data } = supabase.storage.from('whatsapp_media').getPublicUrl(uploadData.path);
  return data.publicUrl;
}

export interface SendWhatsappMessageParams {
  leadId: number;
  companyId: number;
  phone?: string;
  message: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  media: { data: string; type: string; name: string } | null;
  instanceId: string;
  signMessage: boolean;
}

// Retorna o messageId da WuzAPI (usado para rastrear confirmação de
// entrega/leitura via webhook). Lança se a Edge Function retornar erro —
// o chamador decide como tratar (mensagem amigável, rollback otimista).
export async function sendWhatsappMessage(params: SendWhatsappMessageParams): Promise<{ error: unknown; messageId?: string }> {
  if (!supabase) throw new Error('Supabase not initialized');
  const response = await supabase.functions.invoke('whatsapp-send', {
    body: {
      lead_id: params.leadId,
      company_id: params.companyId,
      phone: params.phone,
      message: params.message,
      media: params.media,
      instance_id: params.instanceId,
      sign_message: params.signMessage
    }
  });
  return { error: response.error, messageId: response.data?.messageId };
}

export interface SaveOutboundMessageParams {
  companyId: number;
  leadId: number;
  userId: string;
  messageBody: string;
  messageType: string;
  mediaUrl: string | null;
  instanceId: string;
  whatsappMessageId?: string;
}

export async function saveOutboundMessage(params: SaveOutboundMessageParams) {
  if (!supabase) throw new Error('Supabase not initialized');
  // upsert por message_id: o eco da própria mensagem pode chegar pelo webhook antes deste insert
  // (e até cair em outro lead duplicado). Antes isso estourava erro de chave duplicada e a
  // mensagem "sumia" do chat mesmo já tendo sido enviada.
  const { data, error } = await supabase
    .from('lead_whatsapp_messages')
    .upsert({
      company_id: params.companyId,
      lead_id: params.leadId,
      user_id: params.userId,
      message_body: params.messageBody,
      direction: 'OUTBOUND',
      message_type: params.messageType,
      media_url: params.mediaUrl,
      // status não é enviado: o padrão do banco é SENT e o upsert não pode rebaixar uma
      // mensagem que o recibo do WhatsApp já marcou como entregue/lida.
      instance_id: params.instanceId,
      message_id: params.whatsappMessageId // Store ID for receipt tracking
    }, { onConflict: 'message_id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export interface SyncHistoryParams {
  leadId: number;
  companyId: number;
  phone?: string;
}

export async function syncWhatsappHistory({ leadId, companyId, phone }: SyncHistoryParams) {
  if (!supabase) throw new Error('Supabase not initialized');
  return supabase.functions.invoke('whatsapp-sync-history', {
    body: { lead_id: leadId, company_id: companyId, phone }
  });
}

export async function fetchLeadMessages(leadId: number): Promise<LeadWhatsappMessage[]> {
  if (!supabase) return [];
  const { data } = await supabase
    .from('lead_whatsapp_messages')
    .select('*')
    .eq('lead_id', leadId)
    .order('created_at', { ascending: true });
  return data || [];
}

// Reassina, na WuzAPI, todas as instâncias da empresa nos eventos usados pelo CRM
// (inclui os recibos de entrega/leitura). Idempotente.
export async function ensureWhatsappWebhooks(companyId: number): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.functions.invoke('whatsapp-manager', {
    body: { action: 'ENSURE_WEBHOOKS', instanceName: 'all', companyId }
  });
  if (error) throw error;
}
